
import type { SegmentUrlResolver } from './segmentUrlCache';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { ByteSink, ProgressFunc, SegmentFetcher } from '@mimir/common/format/mp4Subclip';
import { muxSubclipToStream } from '@mimir/common/format/mp4Subclip';
import type { BlobView} from '@mimir/common/io/blobView';
import { Offset } from '@mimir/common/io/blobView';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { AxiosInstance, CancelTokenSource } from 'axios';
import Axios from 'axios';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import { allExceptionDetails } from '@mimir/common/util/exception';

export class CancelTokenManager {
  private allCts: Record<string, CancelTokenSource> = {};

  setUpSource(id: string) {
    const newSource = Axios.CancelToken.source();
    this.allCts[id] = newSource;
    return newSource;
  }

  cleanUpSource(id: string) {
    delete this.allCts[id];
  }

  cancelAll() {
    Object.keys(this.allCts).forEach(id => this.allCts[id]!.cancel(`Cancelling token id: ${id}`));
  }
}

export class BrowserBlob implements BlobView {
  constructor(readonly blob: Blob, readonly start: Offset, readonly end: number) {
  }
  arrayBuffer(): Promise<ArrayBuffer> {
    return this.blobObject().arrayBuffer();
  }

  blobObject(): Blob {
    return this.blob.slice(this.start.position, this.end);
  }

  offset(): Offset {
    return this.start;
  }
  slice(start?: number, end?: number): BlobView {
    const newStart = this.start.add(start ?? 0);
    // `end` (when given) is relative to this view's start, matching the
    // BlobView.slice contract; an omitted `end` keeps the current absolute end.
    const absEnd = end != null ? this.start.position + end : this.end;
    return new BrowserBlob(this.blob, newStart, absEnd);
  }

  tryExtend(after: BlobView): BlobView | null {
    const other: BlobView & Partial<BrowserBlob> = after;
    if (
      this.blob == other.blob &&
      this.end == other.start?.position &&
      other.end != null
    ) {
      return new BrowserBlob(this.blob, this.start, other.end);
    }

    return null;
  }

  get size() {
    return this.end - this.start.position;
  }
}

export class AxiosSegmentFetcher implements SegmentFetcher {
  constructor(protected axios: AxiosInstance, protected segmentResolver: SegmentUrlResolver, protected itemId: string, protected cancelTokenManager: CancelTokenManager,
    protected streamRoleId: StreamRoleId) {
  }

  protected async cancellableFetch(url: string, index: number, responseType: 'arraybuffer' | 'blob') {
    const ctsId = `${this.itemId}-${this.streamRoleId}-${index}`;
    let result;
    try {
      const cts = this.cancelTokenManager.setUpSource(ctsId);
      const cancelToken = cts.token;
      const backoff = new ExponentialBackoff({ timeLimit: 10 * 60 * 1000 });
      result = await backoff.resiliently(
        async () => {
          return this.axios.get(url, {
            responseType,
            timeout: 3 * 60 * 1000,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            cancelToken,
          });
        },
        cancelToken,
        (ms, cause, failureCount) => {
          console.warn(`cancellableFetch for ${url} failed ${failureCount} times. retrying. `, JSON.stringify({ ...allExceptionDetails(cause), retryMs: Math.round(ms), failureCount, stack: undefined }));
        }
      );
    } finally {
      this.cancelTokenManager.cleanUpSource(ctsId);
    }

    return result;
  }

  async fetchSegmentTask(index: number): Promise<() => Promise<BlobView>> {
    const url = await this.segmentResolver.getMediaSegmentUrl(this.itemId, this.streamRoleId, index);
    return async () => {
      // Fetch as a browser Blob (disk-backed) rather than an in-memory ArrayBuffer,
      // so the muxer can slice it zero-copy and stream the output to disk without
      // accumulating multi-GB downloads in the JS heap.
      const response = await this.cancellableFetch(url, index, 'blob');
      const blob: Blob = response.data;
      return new BrowserBlob(blob, new Offset(url, 0), blob.size);
    };
  }

  close(): Promise<unknown> {
    return Promise.resolve();
  }
}

/**
 * Fetch and mux a subclip straight into a byte sink (e.g. a File System Access
 * writable stream), one fragment at a time. This never holds the whole output
 * in memory, so it is safe for multi-GB downloads. The caller owns the sink
 * lifecycle (close on success, abort on failure or cancellation).
 */
export async function streamSubclipToSink(segmentResolver: SegmentUrlResolver, itemId: string,
  segmentDuration: Fraction, firstSegmentIndex: number, lastSegmentIndex: number,
  prependSegmentZero: boolean, progress: ProgressFunc,
  streamRoleId: StreamRoleId, ctm: CancelTokenManager, sink: ByteSink): Promise<void> {
  const fetcher = new AxiosSegmentFetcher(Axios, segmentResolver, itemId, ctm, streamRoleId);
  await muxSubclipToStream(fetcher, segmentDuration, firstSegmentIndex, lastSegmentIndex, prependSegmentZero, sink, progress);
}
